import { LogLevel, Configuration, BrowserCacheLocation } from '@azure/msal-browser';
import { environment } from '../../environments/environment';

const authorityHostname = new URL(environment.auth.authority).hostname;

// environment.auth.cacheLocation / .logLevel are typed as narrow string literals
// per-environment (`as const` in environment.ts/.prod.ts); widen to string before
// comparing so this file compiles regardless of which literal a given environment
// file happens to pin.
const cacheLocationSetting: string = environment.auth.cacheLocation;
const logLevelSetting: string = environment.auth.logLevel;

export const msalConfig: Configuration = {
  auth: {
    clientId: environment.auth.clientId,
    authority: environment.auth.authority,
    knownAuthorities: [authorityHostname],
    redirectUri: environment.auth.redirectUri,
    postLogoutRedirectUri: environment.auth.postLogoutRedirectUri,
  },
  cache: {
    cacheLocation: cacheLocationSetting === 'localStorage'
      ? BrowserCacheLocation.LocalStorage
      : BrowserCacheLocation.SessionStorage,
  },
  system: {
    loggerOptions: {
      loggerCallback: () => {},
      logLevel: logLevelSetting === 'Verbose' ? LogLevel.Verbose : LogLevel.Warning,
      piiLoggingEnabled: environment.auth.piiLoggingEnabled,
    },
    allowRedirectInIframe: false,
  },
};

export const loginRequest = {
  scopes: [environment.auth.apiScope],
  redirectUri: environment.auth.redirectUri,
};

// ---------------------------------------------------------------------------
// Partner (VAR) rail — CIAM tenant. A SECOND MSAL configuration, distinct from the
// workforce one above (different clientId AND tenant → per-request authority is
// impossible, so two PublicClientApplication instances are required). This config
// backs the standalone instance owned by CiamAuthService; it is NOT wired into
// msal-angular's MSAL_INSTANCE DI (that stays the workforce instance).
// ---------------------------------------------------------------------------
export interface CiamAuthConfig {
  clientId: string;
  authority: string;
  knownAuthority: string;
  apiScope: string;
  redirectUri: string;
}

// `null` when the active environment has no CIAM app registration (prod, until the
// prod CIAM reg is created). A null config disables the partner rail end-to-end:
// CiamAuthService builds no instance, the /login partner button hides, and the guard
// stays staff-only. Assigning environment.ciamAuth (a concrete object in dev/local,
// `null` in prod) into this typed constant is checked per build config.
// Cast (not just the annotation) so TS keeps the UNION type: at the production build
// `environment.prod.ts` sets `ciamAuth: null`, whose literal `null` type would otherwise
// narrow this const to `null`, making the `ciamAuth ? {...}` truthy branch below `never`
// (TS2339 on every property access). The cast preserves `CiamAuthConfig | null`. Runtime-neutral.
const ciamAuth = environment.ciamAuth as CiamAuthConfig | null;

// The MSAL Configuration for the CIAM instance, or null when unconfigured. Mirrors
// msalConfig above (sessionStorage cache, redirect-in-iframe disabled). knownAuthorities
// must carry the ciamlogin.com host so MSAL trusts the non-login.microsoftonline.com
// authority; omitting it makes MSAL reject the authority as untrusted.
export const ciamMsalConfig: Configuration | null = ciamAuth
  ? {
      auth: {
        clientId: ciamAuth.clientId,
        authority: ciamAuth.authority,
        knownAuthorities: [ciamAuth.knownAuthority],
        redirectUri: ciamAuth.redirectUri,
        postLogoutRedirectUri: new URL(ciamAuth.redirectUri).origin,
      },
      cache: {
        cacheLocation: cacheLocationSetting === 'localStorage'
          ? BrowserCacheLocation.LocalStorage
          : BrowserCacheLocation.SessionStorage,
      },
      system: {
        loggerOptions: {
          loggerCallback: () => {},
          logLevel: logLevelSetting === 'Verbose' ? LogLevel.Verbose : LogLevel.Warning,
          piiLoggingEnabled: environment.auth.piiLoggingEnabled,
        },
        allowRedirectInIframe: false,
      },
    }
  : null;

// The scope set requested on the CIAM login/token calls — user_impersonation on the
// Support API app so the access token's aud == BT_SUPPORT_AUTH_AUDIENCE. Null when
// the partner rail is unconfigured.
export const ciamLoginRequest = ciamAuth
  ? { scopes: [ciamAuth.apiScope], redirectUri: ciamAuth.redirectUri }
  : null;
